import React from 'react';
import type { WeldJoint, WeldStepPanels } from '../../SpoolsProduction';
import { spoolTw } from '../../spoolUiPalette';
import { effectiveWeldWorkflowKey } from './weldStepPanelHydration';

/** Server / UI step keys from `workflowStatus` (see `WeldJointWorkflowStatus` on API). */
const WORKFLOW_KEYS = [
  'fitup',
  'preheat',
  'welding',
  'vt',
  'ndt',
  'coating',
  'accept',
  'pending',
] as const;

type WorkflowKey = (typeof WORKFLOW_KEYS)[number];

const WORKFLOW_LABEL: Record<WorkflowKey, string> = {
  pending: 'Not started',
  fitup: 'Fit-up',
  preheat: 'Preheat',
  welding: 'Welding',
  vt: 'VT',
  ndt: 'NDT',
  coating: 'Coating',
  accept: 'Accept',
};

/** Colours follow spool stepper: teal = late / pass, orange = active band. */
const WORKFLOW_CLS: Record<WorkflowKey, string> = {
  pending: 'bg-slate-100 text-slate-600 border-slate-200',
  fitup: 'bg-sky-50 text-sky-900 border-sky-200',
  preheat: 'bg-amber-50 text-amber-900 border-amber-200',
  welding: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  vt: 'bg-teal-50 text-teal-900 border-teal-200',
  ndt: 'bg-violet-50 text-violet-900 border-violet-200',
  coating: 'bg-indigo-50 text-indigo-900 border-indigo-200',
  accept: 'bg-[#E8F2EF] text-[#1E6353] border-[#C8D4D0]',
};

function isSubmittedMode(m: string | undefined | null): boolean {
  if (m == null) return false;
  const s = m.trim().toLowerCase();
  return (
    s === 'submitted' ||
    s === 'submit' ||
    s === 'final' ||
    s === 'complete' ||
    s === 'confirm_weld' ||
    s === 'pass_vt' ||
    s === 'pass_release' ||
    s === 'accept_complete'
  );
}

function normalizeWorkflowKey(raw: unknown): WorkflowKey {
  const t = String(raw ?? '')
    .trim()
    .toLowerCase();
  if ((WORKFLOW_KEYS as readonly string[]).includes(t)) {
    return t as WorkflowKey;
  }
  return 'pending';
}

function panelForCurrentStep(
  panels: WeldStepPanels | null | undefined,
  step: WorkflowKey
): { submissionMode: string } | { submissionMode: string }[] | null {
  if (!panels) return null;
  switch (step) {
    case 'fitup':
      return panels.fitUp && panels.fitUp.length > 0 ? panels.fitUp : null;
    case 'preheat':
      return panels.preheat && panels.preheat.length > 0 ? panels.preheat : null;
    case 'welding':
      return panels.welding && panels.welding.length > 0 ? panels.welding : null;
    case 'vt':
      return panels.vt && panels.vt.length > 0 ? panels.vt : null;
    case 'ndt':
      return panels.ndt && panels.ndt.length > 0 ? panels.ndt : null;
    case 'coating':
      return panels.coating && panels.coating.length > 0 ? panels.coating : null;
    case 'accept':
      return panels.accept && panels.accept.length > 0 ? panels.accept : null;
    case 'pending':
    default:
      return null;
  }
}

function currentStepSubmitted(panels: WeldStepPanels | null | undefined, step: WorkflowKey): boolean {
  const p = panelForCurrentStep(panels, step);
  if (p == null) return false;
  if (Array.isArray(p)) {
    return p.some((row) => isSubmittedMode(row.submissionMode));
  }
  return isSubmittedMode(p.submissionMode);
}

const PIPELINE_LABEL: Record<WeldJoint['status'], string> = {
  accepted: 'Accepted',
  ndt_pass: 'NDT pass',
  welded: 'Welded',
  pending: 'Pending',
};

/**
 * Pipeline-only chip (no workflow context). Use when you only have `status`.
 */
export function weldStatusBadge(status: WeldJoint['status']) {
  const map: Record<WeldJoint['status'], { label: string; cls: string }> = {
    accepted: { label: 'Accepted', cls: spoolTw.weldPassBadge },
    ndt_pass: { label: 'NDT Pass', cls: spoolTw.weldPassBadge },
    welded: { label: 'Welded', cls: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    pending: { label: 'Pending', cls: 'bg-gray-100 text-gray-500 border-gray-200' },
  };
  const cfg = map[status];
  return (
    <span className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

/**
 * Table / header cell: current **workflow** step from `effectiveWeldWorkflowKey` (per-weld
 * `weldStepPanels` first; API `accept` is ignored unless this joint has `weldStepPanels.accept`),
 * with a second line for **pipeline** `status` when the weld record has moved past “pending”.
 */
export function weldJointStatusCell(weld: WeldJoint) {
  if (String(weld.id).startsWith('local-wt-')) {
    return <span className="inline-block">{weldStatusBadge('pending')}</span>;
  }

  /** Per-weld: `weldStepPanels` + rules in `effectiveWeldWorkflowKey` (API `accept` without a joint accept row is ignored). */
  const stepKey = normalizeWorkflowKey(effectiveWeldWorkflowKey(weld));
  if (stepKey === 'pending') {
    return <span className="inline-block">{weldStatusBadge(weld.status)}</span>;
  }
  const label = WORKFLOW_LABEL[stepKey];
  const cls = WORKFLOW_CLS[stepKey];
  const submitted = currentStepSubmitted(weld.weldStepPanels ?? null, stepKey);
  const stepNote = submitted ? ' · Submitted' : ' · In progress';

  const showPipelineSub = weld.status !== 'pending';

  return (
    <div className="flex min-w-0 max-w-[220px] flex-col gap-0.5">
      <span
        className={`inline-flex w-fit max-w-full items-center rounded border px-2 py-0.5 text-[11px] font-semibold leading-tight ${cls}`}
        title={
          `Current workflow step: ${label}${stepNote ? stepNote.replace(' ·', ' —') : ''}`.trim()
        }
      >
        <span className="truncate">
          {label}
          <span className="font-normal opacity-90">{stepNote}</span>
        </span>
      </span>
      {/* {showPipelineSub ? (
        <span className="text-[10px] leading-tight text-gray-500" title="Weld line record (pipeline)">
          Record: {PIPELINE_LABEL[weld.status] ?? weld.status}
        </span>
      ) : null} */}
    </div>
  );
}
